/* ═══════════════════════════════════════════════════════════════════════════
   WHO THE CUSTOMER IS, AND HOW WELL WE KNOW IT

   When somebody authorises a payment they have identified themselves, and the
   app should stop pretending it does not know who they are. That is the whole
   point of this file: an account is not something a customer fills in
   afterwards, it is something that already exists the moment the money was
   authorised.

   But "identified" is not one thing, and flattening it into a single
   `verified: true` would be the easiest lie in this codebase to tell.

   ── Three levels, and what each one actually proves ───────────────────────

   **vipps** — Vipps Userinfo returns the name, the date of birth and the
   address, and the name and date of birth are the ones held in
   Folkeregisteret. This is a real identity check: the person proved who they
   are with something only they hold.

   **card** — a card authorisation proves that somebody was able to use that
   card. It identifies a *payment instrument*, not a person. The name on a
   card is whatever the issuer printed on it; it is not checked against any
   register, and a card can be used by somebody it was not issued to. So a
   card gets a name and nothing else, and the screens say "identified for
   payment" rather than "verified".

   **self** — typed in by hand. Useful, and worth keeping, and proves nothing
   at all.

   Getting this ordering wrong is how a platform ends up telling a business
   that a customer's identity was confirmed when all that happened is that a
   card went through. Where age is a condition — alcohol, some treatments —
   that difference is the whole thing, and `domain/disclosure.js` is written
   to ask for the level rather than for a boolean.
   ═══════════════════════════════════════════════════════════════════════ */

/** In order of what they prove. `LEVELS.indexOf` is a comparison. */
export const LEVELS = ['none', 'self', 'card', 'vipps'];

export const METHOD = {
  vipps: {
    id: 'vipps',
    label: 'Verified with Vipps',
    short: 'Vipps',
    /* What came back, so a screen can say what it has rather than guess. */
    gives: ['name', 'date_of_birth', 'address', 'email'],
    proves: 'Name and date of birth are the ones held in Folkeregisteret.',
    verified: true,
  },
  card: {
    id: 'card',
    label: 'Identified by card',
    short: 'Card',
    gives: ['name'],
    /* Said plainly, because the temptation to round this up is exactly the
       thing this file exists to stop. */
    proves: 'A card authorisation shows the card was usable. It is not an identity check.',
    verified: false,
  },
  self: {
    id: 'self',
    label: 'Filled in by hand',
    short: 'Self-declared',
    gives: ['name', 'date_of_birth', 'address', 'email'],
    proves: 'Nothing here has been checked against any register.',
    verified: false,
  },
};

/** The level a customer record sits at. */
export function levelOf(customer) {
  if (!customer) return 'none';
  if (customer.source === 'vipps') return 'vipps';
  if (customer.source === 'card') return 'card';
  if (customer.name) return 'self';
  return 'none';
}

/** Is this at least as strong as the level asked for? */
export function atLeast(customer, level) {
  return LEVELS.indexOf(levelOf(customer)) >= LEVELS.indexOf(level);
}

/** The method record for a customer, or null. */
export function methodOf(customer) {
  const l = levelOf(customer);
  return METHOD[l] || null;
}

/** Is the person signed in at all? A number alone is not a person — it is a
    device that remembers one, which is a different and weaker thing. */
export const isSignedIn = customer => levelOf(customer) !== 'none';

/**
 * The customer record an authorisation produces.
 *
 * `from` is what the payment method handed back. Vipps gives the lot; a card
 * gives a name. Nothing is invented for the fields a method does not return —
 * an address the platform guessed is worse than no address, because a tenant
 * would act on it.
 */
export function fromAuthorisation({ phone, method, person = {} }) {
  const m = METHOD[method];
  if (!m) throw new Error(`Unknown identification method "${method}".`);

  const kept = {};
  for (const field of m.gives) {
    if (person[field] != null) kept[field] = person[field];
  }

  return {
    uid: phone,
    phone,
    ...kept,
    source: m.id,
    verified: m.verified,
    identified_at: new Date().toISOString(),
    created: new Date().toISOString(),
  };
}

/** Short sentence for the screen, in the customer's own direction. */
export function words(customer) {
  const m = methodOf(customer);
  if (!m) return 'Not signed in.';
  return `${m.label}. ${m.proves}`;
}

/** The first name, for a greeting. A greeting that uses the full legal name
    reads like a letter from the council. */
export function firstName(customer) {
  const n = String(customer?.name || '').trim();
  return n ? n.split(/\s+/)[0] : null;
}

/** Initials, for the chip in the header. */
export function initialsOf(customer) {
  const n = String(customer?.name || '').trim();
  if (!n) return '·';
  return n.split(/\s+/).filter(Boolean).slice(0, 2)
    .map(w => w[0]).join('').toUpperCase();
}
